import { FiGlobe } from 'react-icons/fi';

export const spellingUSUKData = {
  courseId: 'spelling-usuk',
  courseType: 'vocabulary',
  title: 'US vs UK Spelling',
  icon: FiGlobe,
  overview: {
    impact: 'Avoid spelling errors in writing',
    focus: 'British and American spelling differences',
    uses: 'Writing Task 1 & 2',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'Why Spelling Matters',
      content: `<p>IELTS accepts both British and American spelling. Both are valid. What matters is <strong>consistency</strong>: do not mix the two systems in the same answer. Mixing spellings can affect your Lexical Resource score.</p>`,
      warning: 'Pick British or American and use that system for the whole test. Do not mix them.',
    },
    {
      title: 'Common Spelling Differences',
      content: `<p>Learn these key patterns:</p>`,
      table: {
        headers: ['Pattern', 'British', 'American'],
        rows: [
          ['-our vs -or', 'colour, favour, behaviour', 'color, favor, behavior'],
          ['-ise vs -ize', 'organise, realise, recognise', 'organize, realize, recognize'],
          ['-re vs -er', 'centre, metre, theatre', 'center, meter, theater'],
          ['-ce vs -se', 'defence, licence, offence', 'defense, license, offense'],
          ['-ogue vs -og', 'catalogue, dialogue', 'catalog, dialog'],
          ['Double L', 'travelling, cancelled', 'traveling, canceled'],
        ],
      },
    },
    {
      title: 'Words That Are the Same',
      content: `<p>Many words are spelled the same in both varieties:</p>`,
      points: [
        'Important, development, government',
        'Education, technology, environment',
        'However, although, therefore',
        'Most academic vocabulary is identical',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Spelling',
      type: 'select',
      instructions: 'Choose whether each word is British or American spelling.',
      questions: [
        {
          text: 'organisation',
          options: ['British', 'American'],
          answer: 'British',
          explanation: 'British uses -isation, American uses -ization.',
        },
        {
          text: 'center',
          options: ['British', 'American'],
          answer: 'American',
          explanation: 'American uses -er, British uses -re (centre).',
        },
        {
          text: 'behaviour',
          options: ['British', 'American'],
          answer: 'British',
          explanation: 'British uses -our, American uses -or (behavior).',
        },
        {
          text: 'canceled',
          options: ['British', 'American'],
          answer: 'American',
          explanation: 'American uses single L, British uses double L (cancelled).',
        },
        {
          text: 'analyse',
          options: ['British', 'American'],
          answer: 'British',
          explanation: 'British uses -yse, American uses -yze (analyze).',
        },
        {
          text: 'defense',
          options: ['American', 'British'],
          answer: 'American',
          explanation: 'American uses -se, British uses -ce (defence).',
        },
      ],
    },
    {
      title: 'Convert to British Spelling',
      type: 'fill-in',
      instructions: 'Write the British spelling of each American word.',
      questions: [
        {
          text: 'color',
          answer: 'colour',
          explanation: 'British uses -our instead of -or.',
        },
        {
          text: 'realize',
          answer: 'realise',
          explanation: 'British uses -ise instead of -ize.',
        },
        {
          text: 'theater',
          answer: 'theatre',
          explanation: 'British uses -re instead of -er.',
        },
        {
          text: 'traveling',
          answer: 'travelling',
          explanation: 'British doubles the L before -ing.',
        },
      ],
    },
    {
      title: 'Spot the Inconsistency',
      type: 'select',
      instructions: 'Which sentence has inconsistent British/American spelling?',
      questions: [
        {
          text: 'Which is INCONSISTENT?',
          options: [
            'The organisation has a significant favour with the center.',
            'The organization has a significant favor with the center.',
            'The organisation has a significant favour with the centre.',
          ],
          answer: 'The organisation has a significant favour with the center.',
          explanation: '"organisation" and "favour" are British, but "center" is American.',
        },
        {
          text: 'Which is INCONSISTENT?',
          options: [
            'The traveler analyzed the theatre program.',
            'The traveller analysed the theatre programme.',
            'The traveler analyzed the theater program.',
          ],
          answer: 'The traveler analyzed the theatre program.',
          explanation: '"traveler" and "analyzed" are American, but "theatre" is British.',
        },
      ],
    },
    {
      title: 'Choose Correct Spelling',
      type: 'select',
      instructions: 'Choose the correct British spelling.',
      questions: [
        {
          text: 'The results were ___ last week.',
          options: ['analysed', 'analyzed'],
          answer: 'analysed',
          explanation: 'British spelling uses -yse (analysed).',
        },
        {
          text: 'We need to ___ the problem.',
          options: ['recognise', 'recognize'],
          answer: 'recognise',
          explanation: 'British spelling uses -ise (recognise).',
        },
        {
          text: 'The city ___ is very old.',
          options: ['centre', 'center'],
          answer: 'centre',
          explanation: 'British spelling uses -re (centre).',
        },
        {
          text: 'She received a ___ for her work.',
          options: ['honour', 'honor'],
          answer: 'honour',
          explanation: 'British spelling uses -our (honour).',
        },
      ],
    },
    {
      title: 'Correct the Essay',
      type: 'writing',
      instructions: 'Rewrite each sentence with consistent British spelling.',
      questions: [
        {
          text: '"The organization has analyzed the data from the research centre."',
          example: 'The organisation has analysed the data from the research centre.',
          tip: 'Change -ization to -isation and -yzed to -ysed for British.',
        },
        {
          text: '"The traveler cancelled his trip to the theater."',
          example: 'The traveller cancelled his trip to the theatre.',
          tip: 'British doubles L in traveller and uses -re in theatre.',
        },
      ],
    },
  ],
  tips: [
    'Choose British OR American and be consistent — mixing is the problem',
    'Both systems are accepted. A fully American essay is fine if it stays American',
    'British spelling is more common in IELTS materials, so it is a useful default if you are unsure',
    'Focus on the common patterns, not memorizing every word',
  ],
  dailyPractice: [
    'Write a paragraph using consistent spelling',
    'Check your essays for spelling consistency',
    'Learn the main patterns, not individual words',
    'Practice common IELTS vocabulary with correct spelling',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'US vs UK Spelling: Understanding British and American spelling differences for consistent IELTS writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...spellingUSUKData,
      title: spellingUSUKData.title + ' (Foundations)',
      overview: {
        ...spellingUSUKData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: spellingUSUKData.sections.slice(0, Math.max(2, Math.ceil(spellingUSUKData.sections.length / 2))),
      exercises: spellingUSUKData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...spellingUSUKData,
      title: spellingUSUKData.title + ' (Advanced)',
      overview: {
        ...spellingUSUKData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: spellingUSUKData.sections,
      exercises: spellingUSUKData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(spellingUSUKData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return spellingUSUKData;
}
